import React, { useEffect, useRef, useState } from 'react';
import { ArrowRight, Hammer, Leaf, Phone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from '@/components/common/Link';
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = '0px'
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
export default function CraftingYourHaven() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="crafting-your-haven" ref={sectionRef} className="dark bg-background text-foreground relative min-h-[85vh] flex items-center overflow-hidden py-20 lg:py-0">
      {}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-background/90 z-10" />
        <div className="absolute inset-0 bg-[url('https://wpvc-images.s3.us-east-1.amazonaws.com/images/1557890/img/luxury_renovation_overlay.png')] bg-cover bg-center bg-fixed opacity-20 mix-blend-overlay" aria-hidden="true" />
      </div>

      <div className="container relative z-20 mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          
          {}
          <div className={`lg:col-span-7 flex flex-col gap-8 transition-all duration-1000 ease-out transform ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 w-fit">
                <Leaf className="w-4 h-4 text-primary" />
                <span className="text-xs font-medium text-primary uppercase tracking-wider">
                  Premium Property Care
                </span>
              </div>
              
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-extrabold font-sans tracking-tight leading-[1.1] text-white">
                Crafting Your <span className="text-primary">Haven</span>
              </h1>
              
              <p className="text-lg md:text-xl text-muted-foreground max-w-2xl leading-relaxed font-medium">
                Transform your property into a sanctuary with our expert whole-house remodels and precision handyman work. We bring your vision to life with quality craftsmanship.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 mt-2">
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 text-base font-semibold h-14 px-8 shadow-lg shadow-primary/20">
                <Link to="#contact">
                  Request Your Bid
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </Button>
              
              <Button asChild variant="outline" size="lg" className="border-primary/30 text-foreground hover:bg-primary/10 hover:text-primary hover:border-primary text-base font-semibold h-14 px-8 backdrop-blur-sm">
                <Link to="#projects">
                  View Projects
                </Link>
              </Button>
            </div>

            <div className="flex items-center gap-8 pt-4 text-sm text-muted-foreground/80">
              <div className="flex items-center gap-2">
                <div className="p-1.5 rounded-full bg-primary/10">
                  <Hammer className="w-4 h-4 text-primary" />
                </div>
                <span>Licensed & Insured</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="p-1.5 rounded-full bg-primary/10">
                  <Phone className="w-4 h-4 text-primary" />
                </div>
                <span>920.325.6665</span>
              </div>
            </div>
          </div>

          {}
          <div className={`lg:col-span-5 relative h-full min-h-[400px] lg:min-h-[600px] transition-all duration-1000 delay-300 ease-out transform ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
            <div className="relative h-full w-full rounded-3xl overflow-hidden shadow-2xl shadow-black/50 border border-white/5 group">
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent z-10" />
              
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1557890/img/property_vertical.png" alt="Beautiful property with modern renovation" className="h-full w-full object-cover object-center transition-transform duration-700 group-hover:scale-105" />

              {}
              <div className="absolute bottom-8 left-8 right-8 z-20">
                <div className="bg-card/95 backdrop-blur-md border border-white/10 p-4 rounded-xl shadow-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Latest Project</p>
                      <p className="text-white font-semibold">Green Haven Estate</p>
                    </div>
                    <div className="h-10 w-10 rounded-full bg-primary flex items-center justify-center text-primary-foreground">
                      <Leaf className="w-5 h-5" />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {}
            <div className="absolute -top-6 -right-6 w-24 h-24 bg-primary/20 rounded-full blur-2xl -z-10" />
            <div className="absolute -bottom-8 -left-8 w-32 h-32 bg-primary/10 rounded-full blur-3xl -z-10" />
          </div>

        </div>
      </div>
    </section>;
}